/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { Fragment, useEffect, useMemo, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { v4 } from "uuid";

import {
  DragAndDrop,
  Drawer,
  InputAuth,
  LoadingComponent,
  DropdownGlobal,
} from "components";

import { useToast } from "hooks/toast";

import {
  addNewAttachment,
  closeModalAttachmentFile,
  setAttachmentList,
} from "store/modules/attachment/actions";
import { IUpload } from "store/modules/attachment/interface";

import { hearingFileServiceV2 } from "services/hearingFile";

import { formatDateTime } from "utils/dateTime";
import { LogOperation } from "utils/entities/logOperation";
import { formatYmd } from "utils/format";

import { IAttachmentTemplate } from "./interface";
import { Container, LoadingContainer } from "./styles";

const CreateAttachmentTemplateInquerito: React.FC<IAttachmentTemplate> = ({
  openModal,
  setOpenModal,
  hearingId,
  listRecord,
}) => {
  const { userCustomerProfile, containerName } = useSelector(
    (store: any) => store.profile,
  );
  const { closeModal, openEditModal, editItem, attachmentList } = useSelector(
    (store: any) => store.attachment,
  );

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState(false);
  const [type, setType] = useState<any>();
  const [typeError, setTypeError] = useState(false);
  const [file, setFile] = useState<File | null>(editItem ?? null);
  const [fileError, setFileError] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [parentRecord, setParentRecord] = useState<any>(null);
  const [parentRecordError, setParentRecordError] = useState(false);

  const [typeSelected, setTypeSelected] = useState<Record<string, string[]>>({
    "video/*": [".avi", ".mp4", ".mpeg", ".wmv"],
  });

  const dispatch = useDispatch();
  const { addToast } = useToast();
  const { t } = useTranslation();

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " bytes";
    if (bytes < 1024 ** 2) return (bytes / 1024).toFixed(2) + " KB";
    if (bytes < 1024 ** 3) return (bytes / 1024 ** 2).toFixed(2) + " MB";
    if (bytes < 1024 ** 4) return (bytes / 1024 ** 3).toFixed(2) + " GB";
    return (bytes / 1024 ** 4).toFixed(2) + " TB";
  };

  const FILE_SIZE_LIMITS: Record<number, number> = {
    2: 1 * 1024 * 1024 * 1024, // Audio: 1GB
    3: 50 * 1024 * 1024, // Foto: 50MB
    4: 200 * 1024 * 1024, // Documento: 200MB
    5: 2 * 1024 * 1024 * 1024, // Video: 2GB
  };

  const handleDropRejected = (fileRejections: any[]) => {
    const isTooLarge = fileRejections[0]?.errors?.some(
      (e: any) => e.code === "file-too-large",
    );

    if (!isTooLarge) {
      addToast({
        type: "warning",
        title: t("attachment.invalidFileType"),
        description: t("attachment.invalidFileTypeDescription"),
      });
      return;
    }

    const maxBytes = type?.id ? FILE_SIZE_LIMITS[type.id] : undefined;
    addToast({
      type: "warning",
      title: t("attachment.fileTooLarge"),
      description: maxBytes
        ? `${t("attachment.fileTooLargeDescription")} ${formatFileSize(maxBytes)}.`
        : t("attachment.fileTooLargeDescription"),
    });
  };

  const listTypes = [
    { id: 2, value: "Áudio", name: "Áudio" },
    { id: 4, value: "Documento", name: "Documento" },
    { id: 3, value: "Foto", name: "Foto" },
    { id: 5, value: "Vídeo", name: "Vídeo" },
  ];

  const recordOptions = useMemo(() => {
    const records = Array.isArray(listRecord)
      ? listRecord
      : [
          ...(listRecord?.hearingInProgress ?? []),
          ...(listRecord?.hearingFinished ?? []),
          ...(listRecord?.hearingScheduled ?? []),
        ];

    const uniqueRecords = records.filter(
      (item: any, index: number, self: any[]) =>
        item?.id &&
        self.findIndex((record: any) => record?.id === item.id) === index,
    );

    const normalizeOptionText = (value?: string | null) => {
      const text = value?.trim();
      if (!text || text === "undefined" || text === "null") return "";
      return text;
    };

    return uniqueRecords.map((item: any) => {
      const startDate = item.startDate ?? item.createIn ?? item.expectedDate;
      const formattedDate = startDate
        ? `${formatDateTime.getFullDate(startDate)} ${formatDateTime.getFullTime(startDate)}`
        : "";
      const optionParts = [
        normalizeOptionText(item.role),
        normalizeOptionText(item.name),
        formattedDate,
      ].filter(Boolean);

      return {
        id: item.id,
        name:
          optionParts.length === 1 && formattedDate
            ? `Oitiva - ${formattedDate}`
            : optionParts.join(" - ") || "Oitiva",
      };
    });
  }, [listRecord]);

  const onChangeParentRecord = (record: any) => {
    setParentRecord(record);
    setParentRecordError(false);
  };

  const onChangeType = (e: any) => {
    setType(e);
    setFile(null);
    if (!e) return;

    switch (e.id) {
      case 2:
        setTypeSelected({ "audio/*": [".wav", ".mp3"] });
        break;
      case 3:
        setTypeSelected({ "image/*": [".png", ".jpeg", ".jpg"] });
        break;
      case 4:
        setTypeSelected({
          "application/pdf": [".pdf"],
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            [".docx"],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
            ".xlsx",
          ],
          "text/plain": [".txt"],
        });
        break;
      case 5:
        setTypeSelected({ "video/*": [".avi", ".mp4", ".mpeg", ".wmv"] });
        break;
      default:
        setTypeSelected({
          "image/*": [".png", ".jpeg", ".jpg"],
          "audio/*": [".wav", ".mp3"],
          "video/*": [".avi", ".mp4", ".mpeg", ".wmv"],
          "application/pdf": [".pdf"],
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            [".docx"],
        });
    }
  };

  const handleCloseModal = () => {
    setTypeError(false);
    setNameError(false);
    setFileError(false);
    setParentRecordError(false);
    setOpenModal(false);
    setName("");
    setType(null);
    setFile(null);
    setParentRecord(null);
    dispatch(closeModalAttachmentFile(false));
  };

  useEffect(() => {
    if (closeModal) handleCloseModal();
  }, [closeModal]);

  useEffect(() => {
    if (editItem) setName(editItem.filename);
  }, [editItem, openEditModal]);

  // useEffect(() => {
  //   if (recordOptions.length !== 1 || parentRecord) return;

  //   setParentRecord(recordOptions[0]);
  //   setParentRecordError(false);
  // }, [parentRecord, recordOptions]);

  useEffect(() => {
    if (!openEditModal || !editItem || !recordOptions.length) return;

    const selectedRecord = recordOptions.find(
      (record: any) => Number(record.id) === Number(editItem?.parentId),
    );

    setParentRecord(selectedRecord || null);
  }, [editItem, openEditModal, recordOptions]);

  const handleSaveAttachment = async () => {
    if (!type || !file || !name.trim() || file?.size === 0 || !parentRecord) {
      if (!type) setTypeError(true);
      if (!file || file?.size === 0) setFileError(true);
      if (!parentRecord) setParentRecordError(true);
      if (!name.trim()) {
        setName(name.trim());
        setNameError(true);
      }
      return addToast({
        type: "warning",
        title: t("audiences.messages.fillFields"),
        description: t("audiences.messages.fillFieldsDescription"),
      });
    }

    const folder = type.name
      .normalize("NFD")
      .replace(/[^a-zA-Z\s]/g, "")
      .toLowerCase();

    const extension = `.${file.name.split(".").pop()}`.toLowerCase();

    const mimePrefixes = Object.keys(typeSelected).map((k) =>
      k.replace("/*", ""),
    );
    const exts = ([] as string[]).concat(...Object.values(typeSelected));

    const validMime = mimePrefixes.some((p) => file.type.startsWith(p));
    const validExt = exts.includes(extension);

    if (!validMime || !validExt) {
      addToast({
        type: "warning",
        title: t("attachment.invalidFileType"),
        description: `${t("common.add")} (${exts.join(", ")}).`,
      });
      setTypeError(true);
      return;
    }

    const filename = `${v4()}_${moment().format("DDMMYYYYHHmmss")}${extension}`;
    const date = new Date();

    const payload = {
      type: type.id,
      title: name,
      filename: filename,
      duration: 0,
      fileOrder: 0,
      size: file.size,
      hash: v4(),
      createIn: formatYmd(date),
      updateIn: formatYmd(date),
      upload: true,
      device: LogOperation.getDevice(),
      ip: LogOperation.getMachineIP(),
      logPoliceUnitId: userCustomerProfile.id,
      logUserId: LogOperation.getUserId(),
    };

    const form = new FormData();
    form.append("content", file);

    const uploadBody: IUpload = {
      id: payload.filename,
      hearingId: hearingId ? String(hearingId) : "0",
      file: form,
      finished: false,
      payload,
      containerName,
      parentId: parentRecord?.id,
    };

    dispatch(addNewAttachment(uploadBody));

    handleCloseModal();
  };

  const handleUpdateFileName = async () => {
    if (!name.trim()) {
      setName(name.trim());
      setNameError(true);
      return addToast({
        type: "warning",
        title: t("audiences.messages.fillFields"),
        description: t("audiences.messages.fillFieldsDescription"),
      });
    }

    if (editItem) {
      handleCloseModal();
      setShowLoading(true);

      await hearingFileServiceV2.patch(
        { op: "replace", path: "title", value: name },
        editItem.hearingFileId,
      );

      const newList = attachmentList.map((hf: any) =>
        hf.hearingFileId === editItem.hearingFileId
          ? { ...editItem, filename: name }
          : hf,
      );

      dispatch(setAttachmentList(newList));
      setShowLoading(false);

      addToast({
        type: "success",
        title: t("attachments.singular"),
        description: t("attachments.updated"),
      });
    }
  };

  return (
    <Fragment>
      <Drawer
        open={openModal || openEditModal}
        onClose={handleCloseModal}
        onApply={openEditModal ? handleUpdateFileName : handleSaveAttachment}
        title={
          openEditModal
            ? t("attachments.editAttachment")
            : t("attachments.createAttachment")
        }
        paddingLeft={33}
        paddingRight={33}
      >
        <Container>
          <InputAuth
            id="input_file_name"
            label={t("labels.fileName")}
            required
            error={nameError}
            value={name}
            onChange={setName}
          />

          <DropdownGlobal
            width="100%"
            labelName={t("labels.fileType")}
            required
            options={listTypes}
            setSelected={onChangeType}
            wantAnObject
            error={typeError}
            disabled={openEditModal}
            showDefaultValue={!!openEditModal}
            defaultValue={
              editItem
                ? listTypes.find((x) => x.id === editItem.type)?.name
                : ""
            }
          />

          <DropdownGlobal
            width="100%"
            labelName={t("recordings.singular")}
            required
            options={recordOptions}
            setSelected={onChangeParentRecord}
            wantAnObject
            error={parentRecordError}
            editDropdown={parentRecord}
            disabled={openEditModal}
          />

          <DragAndDrop
            editItem={{ ...editItem, name: editItem?.filename }}
            fileError={fileError}
            formatFileSize={formatFileSize}
            isEdit={openEditModal}
            onDrop={(files) => setFile(files[0])}
            onDropRejected={handleDropRejected}
            maxSize={type ? FILE_SIZE_LIMITS[type.id] : undefined}
            file={editItem ? { ...file, name: editItem.filename } : file}
            setFile={setFile as any}
            typeSelected={typeSelected}
            type={type}
          />
        </Container>
      </Drawer>

      {showLoading && (
        <LoadingContainer>
          <LoadingComponent loading={showLoading} />
        </LoadingContainer>
      )}
    </Fragment>
  );
};

export default CreateAttachmentTemplateInquerito;
